import { Link } from "react-router-dom";
import SEO from "../SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "../Breadcrumbs";
import { humanoidIntelligenceLinks, HUMANOID_INTELLIGENCE_UPDATED } from "../../data/humanoidIntelligence";

const baseUrl = "https://www.techniahqservice.com";

function buildDatasetSchema({ title, description, path, recordCount, distributions = [], modifiedTime = HUMANOID_INTELLIGENCE_UPDATED, schemaType = "Dataset" }) {
  return {
    "@context": "https://schema.org",
    "@type": schemaType,
    name: title,
    description,
    url: `${baseUrl}${path}`,
    dateModified: modifiedTime,
    creator: {
      "@type": "Organization",
      name: "TechniaHQRobot",
      url: baseUrl,
    },
    ...(Number.isFinite(recordCount) ? { size: String(recordCount) } : {}),
    ...(distributions.length > 0 ? { distribution: distributions.map((item) => ({ "@type": "DataDownload", encodingFormat: item.encodingFormat, contentUrl: `${baseUrl}${item.path}` })) } : {}),
  };
}

export function EvidenceBadge({ children = "Source-backed" }) {
  return (
    <span className="inline-flex rounded-full border border-cyan-300/25 bg-cyan-300/10 px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-cyan-100">
      {children}
    </span>
  );
}

export function UnknownValue({ value, suffix = "" }) {
  const missing = value === null || value === undefined || value === "";
  return missing ? (
    <span className="text-slate-500">Not publicly disclosed</span>
  ) : (
    <span>{value}{suffix}</span>
  );
}

export function MetricCard({ value, label, detail }) {
  return (
    <article className="border border-white/10 bg-white/[0.035] p-4 sm:p-5">
      <p className="text-2xl font-black tracking-tight text-white sm:text-3xl">{value}</p>
      <p className="mt-1 text-[11px] font-black uppercase tracking-[0.16em] text-cyan-200">{label}</p>
      {detail && <p className="mt-2 text-xs leading-5 text-slate-400">{detail}</p>}
    </article>
  );
}

export function SourceLink({ href, children = "Source" }) {
  if (!href) return <span className="text-slate-500">Source not retained</span>;
  return (
    <a href={href} target="_blank" rel="noreferrer" className="font-bold text-cyan-300 underline decoration-cyan-300/40 underline-offset-4 hover:text-cyan-100">
      {children}
    </a>
  );
}

export default function HumanoidIntelligenceShell({
  title,
  description,
  path,
  eyebrow = "HUMANOID INTELLIGENCE",
  recordCount,
  noindex = false,
  distributions = [],
  modifiedTime = HUMANOID_INTELLIGENCE_UPDATED,
  reviewLabel = "Source dates vary by record",
  schemaType = "Dataset",
  children,
}) {
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Humanoid Intelligence", to: "/humanoid-intelligence" },
    { label: title, to: path },
  ];
  const schemas = [
    buildBreadcrumbSchema(breadcrumbs),
    buildDatasetSchema({ title, description, path, recordCount, distributions, modifiedTime, schemaType }),
  ];

  return (
    <>
      <SEO
        title={`${title} TechniaHQRobot`}
        description={description}
        path={path}
        noindex={noindex}
        modifiedTime={modifiedTime}
        schema={schemas}
      />
      <div className="bg-[#030712] text-slate-100">
        <div className="border-b border-white/10 bg-[#030712]">
          <Breadcrumbs items={breadcrumbs} />
        </div>
        <div className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-40" aria-hidden="true" style={{ backgroundImage: "linear-gradient(rgba(56,189,248,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(56,189,248,.05) 1px,transparent 1px)", backgroundSize: "38px 38px" }} />
          <div className="pointer-events-none absolute left-1/2 top-0 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-blue-600/10 blur-3xl" aria-hidden="true" />

          <section className="site-shell relative py-10 sm:py-14">
            <div className="max-w-5xl">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">{eyebrow}</p>
              <h1 className="mt-4 max-w-5xl text-4xl font-black tracking-[-0.04em] text-white sm:text-6xl">{title}</h1>
              <p className="mt-5 max-w-3xl text-base leading-8 text-slate-300 sm:text-lg">{description}</p>
              <div className="mt-5 flex flex-wrap items-center gap-3 text-xs font-bold text-slate-400">
                <span>{reviewLabel}</span>
                {Number.isFinite(recordCount) && <span>• {recordCount} retained records</span>}
                {noindex && <span className="border border-amber-300/25 bg-amber-300/10 px-2 py-1 text-amber-200">NOINDEX UNTIL DATA COVERAGE IMPROVES</span>}
              </div>
            </div>

            <nav className="mt-8 flex gap-2 overflow-x-auto pb-2" aria-label="Humanoid Intelligence sections">
              {humanoidIntelligenceLinks.map((item) => (
                <Link key={item.path} to={item.path} className={`shrink-0 border px-3 py-2 text-xs font-black transition ${item.path === path ? "border-cyan-300/50 bg-cyan-300/10 text-cyan-100" : "border-white/10 bg-white/[0.035] text-slate-300 hover:border-white/25 hover:text-white"}`}>
                  {item.shortLabel}
                </Link>
              ))}
            </nav>
          </section>

          <div className="site-shell relative pb-16 sm:pb-24">{children}</div>
        </div>
      </div>
    </>
  );
}
